import React from 'react';
import DetailModal from 'components/modals/DetailModal';
import { DetailCardType } from 'types/DetailPageTypes';
import { HeaderButtons } from 'types/ButtonTypes';
import ContactForm from '../../views/contacts/ContactForm';
import PersonForm from '../../views/contacts/PersonForm';
import LocationForm from '../../views/contacts/LocationForm';
import { TabType } from 'types/TabTypes';
import { useSettings } from 'services/settings/SettingsContext';
import { useActiveFeature } from 'services/permissions/permissionChecks';

// Function to determine the client field link url based on its entity type
const determineLinkUrl = (client: any) => {
    if (client && client['entity_type'] && client['id']) {
        const baseUrl = client['entity_type'] === 'organization' ? '/contacts/organization' : '/contacts/person';
        return `${baseUrl}/${client['id']}`;
    }
    return null;
}

// Rename the name of the function:
const ProjectModal: React.FC<{ 
    viewKey?: string, 
    itemId?: number 
    weekStartDate?: Date
}> = ({ viewKey, itemId, weekStartDate }) => {
    const { environmentSettings } = useSettings();
    const showProjectScheduling = environmentSettings?.project_show_scheduling;
    const invoicesActive = useActiveFeature('invoices');

    // Specify the parameters of this detail modal:
    const apiObject = 'project';
    const object = 'project';
    const showStatusBar = false;
    const showSummary = false;

    // Define the header buttons of the modal:
    const headerButtons: HeaderButtons = [
        {   
            type: 'dropdown',
            label: 'button.more_button_label',
            buttons: [
                // {
                //     label: 'button.duplicate_project_button_label',
                //     action: 'duplicate',
                //     restrictedRight: ['only_view'],
                // },
                {   
                    label: 'button.reopen_project_button_label',
                    action: 'reopen_project',
                    restrictedRight: ['only_view'],
                    condition: [{ field: 'closed', values: [true] }]
                },
                {
                    label: 'button.delete_project_button_label',
                    action: 'delete',
                    restrictedRight: ['only_view'],
                    allowedRight: ['can_delete'],
                }
            ]
        },
        {   // Projects may be invoiced at any moment, as a loose invoice or by billing the finished jobs
            type: 'dropdown',
            label: 'button.create_invoice_button_label',
            buttons: [
                {
                    label: 'project.invoice.loose_option_label',
                    action: 'create_project_invoice',
                    actionValue: 'loose',
                    restrictedRight: ['only_view'],
                    allowedFeature: ['invoices'],
                },
                {
                    label: 'project.invoice.jobs_option_label',
                    action: 'create_project_invoice',
                    actionValue: 'jobs',
                    restrictedRight: ['only_view'],
                    allowedFeature: ['invoices'],
                    condition: [{ field: 'has_jobs_to_invoice', values: [true] }],
                },
            ]
        },
        {   
            type: 'primary',
            label: 'button.close_project_button_label',
            action: 'close_project',
            restrictedRight: ['only_view'],
            customClass: 'finished',
            condition: [{ field: 'closed', values: [false] }]
        },
        
    ];

    // Define the sections of the modal
    const sections: DetailCardType[] = [
        {
            component: 'FieldsCard',
            alignment: 'horizontal',
            fieldColumns: [
                {
                    title: 'project.general.client_card_header',
                    fields: [
                        { 
                            type: 'searchselect', 
                            name: 'client',
                            label: 'project.general.client_label',
                            placeholder: 'project.general.client_placeholder',
                            objectName: 'client.general.object_name.singular',
                            query: {
                                // Get all contacts which have the contact type 'contact', 'lead' and 'client'
                                endpoint: 'get_contact_search',
                                params: {
                                    contact_type: ['contact', 'lead', 'client'] 
                                }
                            },
                            postEndpoint: 'post_contact',
                            viewModeLinkUrl: { function: determineLinkUrl },
                            selectionFormat: 'name',
                            optionFormat: {
                                title: { field: 'name', format: (value) => value },
                                subtitle: { field: 'primary_location', format: (value) => value.address, optional: true},
                            },
                            addNewItem: { method: 'modal-form', modalForm: <ContactForm /> }
                        },
                        { 
                            type: 'searchselect', 
                            name: 'reporting_person',
                            label: 'project.general.reporting_person_label',
                            placeholder: 'project.general.reporting_person_placeholder',
                            objectName: 'person.general.object_name.singular',
                            query: {
                                // Only get persons and when a client is selected, get the persons connected to that contact
                                endpoint: 'get_person_search',
                                params: { }
                            },
                            postEndpoint: 'post_contact',
                            dataCondition: [{'client.entity_type': 'organization'}],
                            viewModeLinkUrl: { baseUrl: '/contacts/person' },
                            selectionFormat: 'name',
                            optionFormat: {
                                title: { field: 'name', format: (value) => value },
                            },
                            addNewItem: { method: 'modal-form', modalForm: <PersonForm /> }
                        },
                        { 
                            type: 'searchselect', 
                            name: 'invoice_address',
                            label: 'project.general.invoice_address_label',
                            placeholder: 'project.general.invoice_address_placeholder',
                            objectName: 'location.general.object_name.singular',
                            query: {
                                // Get all locations and when a client is selected, get the primary locations connected to that contact
                                endpoint: 'get_location_search',
                                params: { }
                            },
                            postEndpoint: 'post_location',
                            dataCondition: [{'client.entity_type': 'organization'}],
                            viewModeLinkUrl: { baseUrl: '/contacts/work-locations' },
                            selectionFormat: 'address',
                            optionFormat: {
                                title: { field: 'address', format: (value) => value },
                            },
                            addNewItem: { method: 'modal-form', modalForm: <LocationForm /> }
                        },
                    ]
                },
                {
                    title: 'project.general.work_location_card_header',
                    fields: [
                        { 
                            type: 'searchselect', 
                            name: 'work_location',
                            label: 'project.general.work_location_label',
                            placeholder: 'project.general.work_location_placeholder',
                            objectName: 'location.general.object_name.singular',
                            query: {
                                // Get all locations
                                endpoint: 'get_location_search',
                                params: { }
                            },
                            postEndpoint: 'post_location',
                            viewModeLinkUrl: { baseUrl: '/contacts/work-locations' },
                            selectionFormat: 'address',
                            optionFormat: {
                                title: { field: 'address', format: (value) => value },
                            },
                            addNewItem: { method: 'modal-form', modalForm: <LocationForm /> },
                            tooltipText: 'project.general.work_location_tooltipText',
                        },
                        { 
                            type: 'searchselect', 
                            name: 'location_person',
                            label: 'project.general.contact_at_location_label',
                            placeholder: 'project.general.contact_at_location_placeholder',
                            objectName: 'person.general.object_name.singular',
                            query: {
                                // Get all persons
                                endpoint: 'get_person_search',
                            },
                            postEndpoint: 'post_contact',
                            dataCondition: [
                                {'client.entity_type': 'organization'}
                            ],
                            viewModeLinkUrl: { baseUrl: '/contacts/person' },
                            selectionFormat: 'name',
                            optionFormat: {
                                title: { field: 'name', format: (value) => value },
                            },
                        },
                    ],
                },
            ]
        },
        {
            component: 'FieldsCard',
            alignment: 'horizontal',
            fieldColumns: [
                {
                    title: 'project.general.data_card_header',
                    fields: [
                        {
                            type: 'text',
                            name: 'name',
                            label: 'project.general.name_label',
                            placeholder: 'project.general.name_placeholder'
                        },
                        { 
                            type: 'dropdown', 
                            name: 'workflow', 
                            label: 'project.general.type_short_label', 
                            apiRequest: [{
                                endpoint: 'get_workflow_list',
                                object: 'workflow',
                                params: { is_active: true, connected_feature: 'project' },
                                useCache: true
                            }],
                            selectionFormat: 'name',
                            optionFormat: 'name',
                            orderOptions: true,
                            backendField: 'id',
                            disabled_selected: 'project.general.type_disabled_selected',
                            allowNoneOption: false,
                            showSearch: false,
                        },
                        { 
                            type: 'reference', 
                            name: 'reference',
                            label: 'project.general.reference_label',
                        },
                    ]
                },
                {
                    fields: [
                        { 
                            type: 'dropdown', 
                            name: 'division', 
                            label: 'project.general.division_label', 
                            apiRequest: [{
                                endpoint: 'get_division_list',
                                object: 'division',
                                params: { is_active: true },
                                useCache: true
                            }],
                            activeFeature: 'divisions',
                            disabled_selected: 'project.general.division_disabled_selected',
                            allowNoneOption: false,
                            showSearch: false,
                        },
                        { 
                            type: 'multiselect', 
                            name: 'labels', 
                            label: 'job.general.labels_label', 
                            apiObject: 'label',
                            apiRequest: [{
                                endpoint: 'get_label_list',
                                object: 'label',
                                params: { deleted: false, connected_feature: 'projects' },
                                useCache: true
                            }],
                            allowNoneOption: false,
                            showSearch: false,
                            disabled_selected: 'job.general.labels_disabled_selected',
                        },
                        { 
                            type: 'dropdown', 
                            name: 'managed_by_user', 
                            label: 'job.general.managed_by_user_label', 
                            apiRequest: [{
                                endpoint: 'get_user_list',
                                object: 'office_user',
                                params: { is_active: true, office_access: true },
                                useCache: true
                            }],
                            disabled_selected: 'job.general.managed_by_user_disabled_selected',
                            optionFormat: 'full_name',
                            selectionFormat: 'full_name',
                            allowNoneOption: false,
                            showSearch: false,
                        },
                    ]
                }
            ]
        },
        {
            component: 'FieldsCard',
            alignment: 'horizontal',
            fieldColumns: [
                {
                    title: 'project.general.date_budget_card_header',
                    fields: [
                        { 
                            type: 'dropdown', 
                            name: 'billing_type', 
                            label: 'project.general.billing_type_label',
                            disabled_selected: 'forms.general_disabled_selected',
                            allowNoneOption: false,
                            showSearch: false,
                            hardcodedOptions: [
                                { value: 'calculation', name: 'project.billing_type.calculation'}, 
                                { value: 'fixed', name: 'project.billing_type.fixed'}, 
                                { value: 'non_billing', name: 'project.billing_type.non_billing'}, 
                            ],
                        },
                        { 
                            type: 'price-currency', 
                            name: 'budget', 
                            label: 'project.general.budget_label', 
                            placeholder: 'project.general.budget_placeholder', 
                            apiObject: 'currency',
                        },
                        {
                            type: 'number',
                            name: 'estimated_hours',
                            label: 'project.general.estimated_hours_label', 
                        },
                    ]
                },
                {
                    fields: [
                        {
                            type: 'date',
                            name: 'start_date',
                            label: 'project.general.start_date_label', 
                        },
                        {
                            type: 'date',
                            name: 'end_date',
                            label: 'project.general.end_date_label', 
                        },
                    ]
                }
            ]
        },
        {
            component: 'FieldsCard',
            title: 'project.general.description_header',
            alignment: 'stacked',
            fields: [
                { 
                    type: 'textarea', 
                    name: 'description', 
                    emptyLabel: 'project.general.description_emptyLabel',
                    placeholder: 'project.general.description_placeholder', 
                    rows: 5,
                },
            ]
        }
    ];

    const tabs: TabType[] = [
        ...(showProjectScheduling ? [{
            label: 'project.general.planning_header',
            type: 'project-planning'
        }] : []),
        {
            label: 'project.general.jobs_header',
            type: 'project-jobs'
        },
        {
            label: 'project.general.timerows_header',
            type: 'project-time-rows'
        },
        ...(invoicesActive ? [{
            label: 'project.general.invoices_header',
            type: 'invoices'
        }] : []),
        {
            label: 'project.general.attachments_header',
            type: 'attachments'
        },
        {
            label: 'project.general.photos_header',
            type: 'project-photos'
        },
    ];
    
    return (
        <DetailModal 
            viewKey={viewKey}
            objectType={object}
            apiObject={apiObject}
            itemId={itemId}
            headerButtons={headerButtons}
            statusBar={showStatusBar}
            sections={sections}
            tabs={tabs}
            summary={showSummary}
            extraProps={{
                schedulerWeekStart: weekStartDate
            }}
        />
    );
};

export default ProjectModal;